/**
 * Temporary mock data for the Van Management Dashboard.
 * Replace with API responses once endpoints in constants.js are available.
 */

import { DASHBOARD_COLORS, progressColor } from '../styles/colors';
import { formatAmount } from '../utils/formatters';

const SUMMARY_KPI_CONFIG = [
  { key: 'totalOrdersAmt', apiKey: 'totalOrdersAmt', label: 'Total Revenue', sub: '', variant: 'green', icon: '📋' },
  { key: 'challansCreated', apiKey: 'challanCreated', label: 'Challans Created', sub: '', variant: 'green', icon: '📄' },
  { key: 'pendingSettlements', apiKey: 'pendingSettlement', label: 'Active Challans', sub: 'Open', variant: 'amber', icon: '🧾' },
  { key: 'pendingChallans', apiKey: 'challanPending', label: 'Pending Challans', sub: 'Awaiting creation', variant: 'amber', icon: '⏳' },
  { key: 'assignedVehicles', apiKey: 'assignVehicle', label: 'Assigned Vehicles', sub: 'With challans', variant: 'green', icon: '🚚' },
  { key: 'stockTransferRequests', apiKey: 'stockTransferReq', label: 'Stock Transfer Req.', sub: 'Pending', variant: 'violet', icon: '🔄' },
  { key: 'dispatchQty', apiKey: 'dispatchQty', label: 'Dispatch Qty', sub: 'Units dispatched', variant: 'teal', icon: '📦' },
  { key: 'deliveryRate', apiKey: 'deliveryRate', label: 'Delivery Rate', variant: 'red', icon: '🔴' },
];

const formatSummaryValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '0';
  }
  return String(value);
};

const buildDeliveryRateSub = (apiData = {}) => {
  const settled = formatSummaryValue(apiData.challanSettled);
  const created = formatSummaryValue(apiData.challanCreated);
  return `${settled} challans settled out of ${created}`;
};

const buildTotalOrdersSub = (apiData = {}) => {
  const totalOrders = formatSummaryValue(apiData.totalOrders);
  return `From ${totalOrders} Orders`;
};

const parseDeliveryRate = (value) => {
  const num = Number(String(value ?? '').replace('%', '').trim());
  return Number.isFinite(num) ? num : 0;
};

const getDeliveryRateVariant = (rate) => {
  if (rate > 70) return 'green';
  if (rate >= 35) return 'amber';
  return 'red';
};

const getDeliveryRateIcon = (variant) => {
  if (variant === 'green') return '✅';
  if (variant === 'amber') return '🟠';
  return '🔴';
};

export const buildSummaryKpis = (apiData = {}) =>
  SUMMARY_KPI_CONFIG.map(({ key, apiKey, label, sub, variant, icon }) => {
    const item = {
      key,
      label,
      value: formatSummaryValue(apiData[apiKey]),
      sub:
        key === 'deliveryRate'
          ? buildDeliveryRateSub(apiData)
          : key === 'totalOrdersAmt'
            ? buildTotalOrdersSub(apiData)
            : sub,
      variant,
      icon,
    };

    if (key === 'deliveryRate') {
      const deliveryVariant = getDeliveryRateVariant(parseDeliveryRate(apiData[apiKey]));
      return {
        ...item,
        variant: deliveryVariant,
        icon: getDeliveryRateIcon(deliveryVariant),
      };
    }

    return item;
  });

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const toPercentWidth = (value, total) => {
  if (total <= 0) {
    return '0%';
  }
  return `${Math.round((value / total) * 100)}%`;
};

const mapChallanStatusBadge = (status) => {
  const statusCodeMap = {
    0: { status: 'pending', statusLabel: 'Pending' },
    1: { status: 'dispatched', statusLabel: 'Active' },
    2: { status: 'delivered', statusLabel: 'Closed' },
    3: { status: 'dispatched', statusLabel: 'Dispatched' },
  };

  if (statusCodeMap[status]) {
    return statusCodeMap[status];
  }

  const normalized = String(status ?? '').trim().toLowerCase();
  if (!normalized) {
    return { status: 'pending', statusLabel: 'Pending' };
  }
  if (normalized.includes('deliver')) {
    return { status: 'delivered', statusLabel: String(status) };
  }
  if (normalized.includes('dispatch')) {
    return { status: 'dispatched', statusLabel: String(status) };
  }
  if (normalized.includes('cancel')) {
    return { status: 'pending', statusLabel: String(status) };
  }
  if (normalized.includes('pending')) {
    return { status: 'pending', statusLabel: String(status) };
  }

  return { status: 'pending', statusLabel: String(status) };
};

export const buildChallanStatus = (apiStatus = {}, daySummary = []) => {
  const created = toNumber(apiStatus.challanCreated);
  const pending = toNumber(apiStatus.challanPending);
  const dispatched = toNumber(apiStatus.challanDispatch);
  const delivered = toNumber(apiStatus.challanDelivered);
  const cancelled = toNumber(apiStatus.challanCancel);

  const trendLabels = daySummary.map(
    (day) => day.dayName || day.challanDate || '',
  );
  const trendData = daySummary.map((day) => toNumber(day.challanCount));

  return {
    total: created,
    delivered,
    dispatched,
    pending,
    cancelled,
    donutData: [delivered, dispatched, pending, cancelled],
    trendLabels,
    trendData,
    progressBars: [
      { label: 'Created', value: created, width: '100%', color: DASHBOARD_COLORS.violet },
      { label: 'Active Challans', value: dispatched, width: toPercentWidth(dispatched, created), color: DASHBOARD_COLORS.blue },
      { label: 'Delivered', value: delivered, width: toPercentWidth(delivered, created), color: DASHBOARD_COLORS.green },
      { label: 'Pending', value: pending, width: toPercentWidth(pending, created), color: DASHBOARD_COLORS.amber },
      { label: 'Cancel', value: cancelled, width: toPercentWidth(cancelled, created), color: DASHBOARD_COLORS.red },
    ],
  };
};

export const buildRecentChallans = (apiChallans = []) =>
  apiChallans.map((challan) => {
    const { status, statusLabel } = mapChallanStatusBadge(challan.status);
    const driver = challan.driver || '—';
    const isUnassigned =
      !challan.driver ||
      String(challan.driver).trim().toLowerCase().includes('not assigned');

    return {
      id: challan.id,
      index: challan.index,
      challanNo: challan.challanNo || '—',
      challanDate: challan.challanDate || '—',
      createdTime: challan.createdTime || '—',
      amount: challan.amount,
      driver,
      driverHighlight: isUnassigned ? 'red' : undefined,
      status,
      statusLabel,
    };
  });

export const EMPTY_CHALLAN_STATUS = buildChallanStatus({}, []);

const DRIVER_KPI_CONFIG = [
  { key: 'onRoute', apiKey: 'driverOnRoute', label: 'Drivers On Route', sub: 'Active now', variant: 'blue', icon: '🛣️' },
  { key: 'available', apiKey: 'vehicleAvailable', label: 'Vehicles Available', sub: 'Ready to load', variant: 'green', icon: '✅' },
  { key: 'loaded', apiKey: 'vehicleLoaded', label: 'Vehicles Loaded', sub: 'Dispatched', variant: 'teal', icon: '📦' },
  { key: 'delayed', apiKey: 'inactiveVehicle', label: 'Inactive Vehicles', sub: 'Need attention', variant: 'red', icon: '🚨' },
];

export const buildDriverKpis = (apiData = {}) =>
  DRIVER_KPI_CONFIG.map(({ key, apiKey, label, sub, variant, icon }) => ({
    key,
    label,
    value: formatSummaryValue(apiData[apiKey]),
    sub,
    variant,
    icon,
  }));

const mapDriverStatus = (driver) => {
  const assigned = toNumber(driver.assignedChallan);
  const active = toNumber(driver.activeChallan);

  if (assigned === 0) {
    return { status: 'available', statusLabel: 'Available' };
  }
  if (active > 0) {
    return { status: 'onRoute', statusLabel: 'On Route' };
  }
  return { status: 'available', statusLabel: 'Available' };
};

export const buildDriverUtilization = (apiDrivers = []) =>
  apiDrivers.map((driver) => {
    const assigned = toNumber(driver.assignedChallan);
    const active = toNumber(driver.activeChallan);
    const delivered = toNumber(driver.settledChallan);
    const pending = toNumber(driver.pendingChallan);
    const { status, statusLabel } = mapDriverStatus(driver);

    return {
      id: driver.id,
      index: driver.index,
      name: driver.name || '—',
      assigned,
      active,
      delivered: assigned === 0 ? null : delivered,
      pending: assigned === 0 ? null : pending,
      status,
      statusLabel,
    };
  });

const progressColorForDriver = progressColor;

export const buildDriverProgress = (apiDrivers = []) =>
  apiDrivers
    .filter((driver) => toNumber(driver.assignedChallan) > 0)
    .map((driver) => {
      const assigned = toNumber(driver.assignedChallan);
      const delivered = toNumber(driver.settledChallan);
      const percent =
        assigned > 0 ? Math.round((delivered / assigned) * 100) : 0;

      return {
        id: driver.id,
        name: driver.name || '—',
        delivered,
        assigned,
        percent,
        color: progressColorForDriver(percent),
      };
    });

export const buildStockTransferFunnel = (apiSummary = {}) => [
  { key: 'total', label: 'Total', value: toNumber(apiSummary.total), color: 'amber' },
  { key: 'approved', label: 'Approved', value: toNumber(apiSummary.approved), color: 'blue' },
  {
    key: 'inTransaction',
    label: 'In Transaction',
    value: toNumber(apiSummary.inTransaction),
    color: 'violet',
  },
  {
    key: 'outTransaction',
    label: 'Out Transaction',
    value: toNumber(apiSummary.outTransaction),
    color: 'green',
  },
];

const mapStockInOutStatus = (inOutStatus) => {
  const normalized = String(inOutStatus ?? '').trim().toUpperCase();
  if (normalized === 'IN') {
    return { status: 'in', statusLabel: 'IN' };
  }
  if (normalized === 'OUT') {
    return { status: 'out', statusLabel: 'OUT' };
  }
  return { status: 'requested', statusLabel: inOutStatus || '—' };
};

export const buildPendingTransfers = (apiList = []) =>
  apiList.map((item) => {
    const { status, statusLabel } = mapStockInOutStatus(item.inOutStatus);

    return {
      id: item.id,
      index: item.index,
      transferNo: item.challanNo || '—',
      date: item.challanDate || '—',
      salesTeamName: item.salesTeamName || '—',
      status,
      statusLabel,
      productCount: item.productCount ?? '—',
      qty: item.productQuantity ?? '—',
      amount: item.productAmount,
    };
  });

export const MOCK_INVENTORY_KPIS = [
  { key: 'totalSkus', label: 'Total SKUs', value: '248', sub: 'Active items', variant: 'green', icon: '🗂️' },
  { key: 'lowStock', label: 'Low Stock Items', value: '18', sub: 'Need reorder', variant: 'amber', icon: '⚠️' },
  { key: 'outOfStock', label: 'Out of Stock', value: '6', sub: 'Urgent reorder', variant: 'red', icon: '🔴' },
];

export const MOCK_STOCK_LEVELS = [
  { name: 'Beverages', sub: 'Cold Drinks, Juices', pct: 72, stock: 1440, color: DASHBOARD_COLORS.blue },
  { name: 'Snacks & Namkeen', sub: 'Chips, Biscuits, Nuts', pct: 58, stock: 860, color: DASHBOARD_COLORS.green },
  { name: 'HPC Category', sub: 'Soaps, Detergents', pct: 41, stock: 520, color: DASHBOARD_COLORS.amber },
  { name: 'Dairy Products', sub: 'Milk, Curd, Paneer', pct: 30, stock: 310, color: DASHBOARD_COLORS.red },
  { name: 'Staples', sub: 'Rice, Wheat, Pulses', pct: 85, stock: 2100, color: DASHBOARD_COLORS.violet },
];

const SETTLEMENT_KPI_CONFIG = [
  { key: 'todaySettlements', apiKey: 'totalSettledAmt', label: "Total Settlements", sub: 'Completed', variant: 'green', icon: '✅' },
  { key: 'pendingSettlements', apiKey: 'totalDispatchAmt', label: 'Pending Settlements', sub: 'To be cleared', variant: 'amber', icon: '⏳' },
  { key: 'overdueSettlements', apiKey: 'overdueAmount', label: 'Overdue Settlements', sub: 'Overdue', variant: 'red', icon: '🚨' },
];

const formatSettlementAmount = (value) => formatAmount(value);

export const buildSettlementKpis = (apiData = {}) =>
  SETTLEMENT_KPI_CONFIG.map(({ key, apiKey, label, sub, variant, icon }) => ({
    key,
    label,
    value: formatSettlementAmount(apiData[apiKey]),
    sub,
    variant,
    icon,
  }));

const mapSettlementUrgency = (urgency) => {
  const normalized = String(urgency ?? '').trim().toLowerCase();
  if (normalized === 'high') {
    return { urgency: 'high', urgencyLabel: 'High' };
  }
  if (normalized === 'critical') {
    return { urgency: 'critical', urgencyLabel: 'Critical' };
  }
  return { urgency: 'normal', urgencyLabel: 'Normal' };
};

const formatDaysPending = (days) => {
  const num = toNumber(days);
  if (num === 1) {
    return '1 day';
  }
  return `${num} days`;
};

export const buildPendingSettlements = (apiList = []) =>
  apiList.map((item) => {
    const { urgency, urgencyLabel } = mapSettlementUrgency(item.urgency);

    return {
      id: item.id,
      index: item.index,
      salesman: item.name || '—',
      amount: item.amount,
      daysPending: formatDaysPending(item.daysPending),
      urgency,
      urgencyLabel,
    };
  });

export const INITIAL_DASHBOARD_DATA = {
  summary: buildSummaryKpis({}),
  challanStatus: EMPTY_CHALLAN_STATUS,
  recentChallans: [],
  driverKpis: buildDriverKpis({}),
  driverUtilization: [],
  driverProgress: [],
  stockTransferFunnel: buildStockTransferFunnel({}),
  pendingTransfers: [],
  inventoryKpis: MOCK_INVENTORY_KPIS,
  stockLevels: MOCK_STOCK_LEVELS,
  settlementKpis: buildSettlementKpis({}),
  pendingSettlements: [],
};
